import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";

const ExplorePage = () => {
    const [couples, setCouples] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState("all");

    // ✅ 1) Parse date sin shift UTC
    const parseDateValue = (value) => {
        if (!value) return null;

        // Firestore Timestamp
        if (typeof value?.toDate === "function") return value.toDate();

        // "YYYY-MM-DD" → Date local
        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [y, m, d] = value.split("-").map(Number);
            return new Date(y, m - 1, d);
        }

        return new Date(value);
    };

    const formatWeddingDate = (value) => {
        const d = parseDateValue(value);
        if (!d) return "";
        return new Intl.DateTimeFormat("es-AR", {
            year: "numeric",
            month: "long",
            day: "numeric",
        }).format(d);
    };

    const getMonthYear = (value) => {
        const d = parseDateValue(value);
        if (!d) return "";
        return new Intl.DateTimeFormat("es-AR", {
            year: "numeric",
            month: "long",
        }).format(d);
    };

    const getMonthKey = (value) => {
        const d = parseDateValue(value);
        if (!d) return 0;
        return d.getFullYear() * 100 + (d.getMonth() + 1); // ej 202603
    };

    useEffect(() => {
        const fetchPublicCouples = async () => {
            try {
                const response = await fetch(
                    "https://gift-get-public-couples-336444799661.us-central1.run.app"
                );
                const data = await response.json();

                if (data.success) setCouples(data.couples || []);
            } catch (error) {
                console.error("Error fetching public couples:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPublicCouples();
    }, []);

    // ✅ 2) Agrupar por mes
    const couplesByMonth = useMemo(() => {
        return (couples || []).reduce((acc, couple) => {
            const monthYear = getMonthYear(couple.weddingDate);
            if (!acc[monthYear]) acc[monthYear] = [];
            acc[monthYear].push(couple);
            return acc;
        }, {});
    }, [couples]);

    // ✅ 3) Ordenar meses correctamente
    const sortedMonths = useMemo(() => {
        return Object.keys(couplesByMonth).sort((a, b) => {
            const keyA = getMonthKey(couplesByMonth[a]?.[0]?.weddingDate);
            const keyB = getMonthKey(couplesByMonth[b]?.[0]?.weddingDate);
            return keyA - keyB;
        });
    }, [couplesByMonth]);

    const filteredMonths =
        selectedMonth === "all"
            ? sortedMonths
            : sortedMonths.filter((m) => m === selectedMonth);

    return (
        <div className="min-h-screen bg-gradient-to-br from-accent-50 via-white to-primary-50">
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-neutral-200 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center">
                        <Logo className="h-12 sm:h-14" />
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full
                         bg-white border border-neutral-200 text-neutral-700
                         hover:bg-neutral-50 transition-colors font-semibold shadow-sm"
                        >
                            ← Volver al inicio
                        </Link>
                    </div>
                </div>
            </header>

            {/* Main */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center mb-12">
                    <h1 className="text-4xl sm:text-5xl font-display font-bold text-neutral-900 mb-4">
                        Explorar Bodas
                    </h1>
                    <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
                        Descubrí las listas de regalos de parejas que se casan próximamente
                    </p>
                </div>

          {/* Filter - Responsive */}
{!loading && sortedMonths.length > 1 && (
  <div className="mb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    {/* ✅ MOBILE: Scroll horizontal con fades */}
    <div className="sm:hidden relative">
      {/* Fade left */}
      <div className="absolute left-0 top-0 h-12 w-8 bg-gradient-to-r from-accent-50/90 to-transparent pointer-events-none z-10" />
      
      {/* Fade right */}
      <div className="absolute right-0 top-0 h-12 w-8 bg-gradient-to-l from-accent-50/90 to-transparent pointer-events-none z-10" />
      
      {/* Scroll container */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory">
        <button
          onClick={() => setSelectedMonth("all")}
          className={`flex-shrink-0 snap-center px-5 py-2.5 rounded-full font-medium transition-colors ${
            selectedMonth === "all"
              ? "bg-accent-500 text-white shadow-md"
              : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
          }`}
        >
          Todos los meses
        </button>

        {sortedMonths.map((month) => (
          <button
            key={month}
            onClick={() => setSelectedMonth(month)}
            className={`flex-shrink-0 snap-center px-5 py-2.5 rounded-full font-medium transition-colors whitespace-nowrap ${
              selectedMonth === month
                ? "bg-accent-500 text-white shadow-md"
                : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            {month}
          </button>
        ))}
      </div>
      
      {/* Indicador */}
      <p className="text-center text-xs text-neutral-400 mt-2">
        ← Deslizá para ver más →
      </p>
    </div>

    {/* ✅ DESKTOP: Flex wrap centrado (sin scroll) */}
    <div className="hidden sm:flex flex-wrap gap-2 justify-center">
      <button
        onClick={() => setSelectedMonth("all")}
        className={`px-5 py-2.5 rounded-full font-medium transition-colors ${
          selectedMonth === "all"
            ? "bg-accent-500 text-white shadow-md"
            : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
        }`}
      >
        Todos los meses
      </button>

      {sortedMonths.map((month) => (
        <button
          key={month}
          onClick={() => setSelectedMonth(month)}
          className={`px-5 py-2.5 rounded-full font-medium transition-colors whitespace-nowrap ${
            selectedMonth === month
              ? "bg-accent-500 text-white shadow-md"
              : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50"
          }`}
        >
          {month}
        </button>
      ))}
    </div>
  </div>
)}
                {/* Loading */}
                {loading && (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
                    </div>
                )}

                {/* Empty */}
                {!loading && couples.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">💍</div>
                        <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                            No hay bodas para mostrar
                        </h3>
                        {/* <p className="text-neutral-600">Sé el primero en crear una lista pública</p>
                        <Link to="/register" className="btn-primary mt-6 inline-block">
                            Crear mi lista
                        </Link> */}
                    </div>
                )}

                {/* Couples by month */}
                {!loading &&
                    filteredMonths.map((monthYear) => (
                        <div key={monthYear} className="mb-12">
                            <h2 className="text-2xl font-display font-bold text-neutral-900 mb-6 flex items-center gap-3">
                                <span className="text-accent-600">📅</span>
                                {monthYear}
                                {/* <span className="text-sm font-normal text-neutral-500">
                  ({couplesByMonth[monthYear].length} boda
                  {couplesByMonth[monthYear].length !== 1 ? "s" : ""})
                </span> */}
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {couplesByMonth[monthYear].map((couple) => (
                                    <Link
                                        key={couple.id}
                                        to={`/lista/${couple.id}`}
                                        className="group overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm
             hover:shadow-[0_18px_60px_-30px_rgba(0,0,0,0.35)]
             hover:border-accent-200 transition-all"
                                    >
                                        {/* Cover */}
                                        <div className="relative h-36 bg-neutral-100">
                                            {couple.profileImage && (
                                                <img
                                                    src={couple.profileImage}
                                                    alt=""
                                                    className="absolute inset-0 h-full w-full object-cover object-[50%_30%] opacity-80"
                                                />
                                            )}

                                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />

                                            {/* Avatar centrado */}
                                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-10">
                                                {couple.profileImage ? (
                                                    <img
                                                        src={couple.profileImage}
                                                        alt={`${couple.brideName} & ${couple.groomName}`}
                                                        className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md bg-white"
                                                    />
                                                ) : (
                                                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-accent-500 to-primary-500
                        border-4 border-white shadow-md flex items-center justify-center">
                                                        <span className="text-2xl font-bold text-white">
                                                            {couple.brideName?.[0]}{couple.groomName?.[0]}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Body */}
                                        <div className="pt-12 p-5 text-center">
                                            <h3 className="text-lg font-display font-semibold text-neutral-900">
                                                {couple.brideName} {couple.brideLastName} & {couple.groomName} {couple.groomLastName}
                                            </h3>

                                            <p className="text-sm text-neutral-500 mt-1">
                                                {formatWeddingDate(couple.weddingDate)}
                                            </p>

                                            <p className="mt-4 text-sm text-accent-600 font-semibold">
                                                Ver lista de regalos →
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
            </main>
        </div>
    );
};

export default ExplorePage;